import type { AxiosRequestConfig } from "axios";
import { gatewayApi } from "./client";
import { gatewayEndpoints, type CsvExportOptions } from "./endpoints";
import type { Client, User, UserRole, Operation, OperationStatus } from "@shared-types";
import type { components } from "@shared-types/api.gen";

// ---------- Aggregated response shapes ----------

/** Paginated list response wrapping `items` and total count metadata. */
export interface PaginatedListResponse<T> {
    items: T[];
    total: number;
    skip: number;
    limit: number;
    has_more: boolean;
}

/** Drive-related metadata flags stored in OperationMetaItem.meta. */
export interface OperationMetaFlags {
    folder_id?: string | null;
    folder_name?: string | null;
    upload_pdfs_to_drive?: boolean;
    upload_graphs_to_drive?: boolean;
    pdf_upload_done?: boolean;
    graph_upload_done?: boolean;
    [key: string]: unknown;
}

/** Enriched operation entry returned by /ytg-meta/operations/all. */
export interface OperationMetaItem {
    ytg_operation_id: number;
    operation: Operation;
    user: User;
    client: Client;
    meta?: OperationMetaFlags;
    [key: string]: unknown;
}

/** Aggregated stats response (computed server-side via SQL GROUP BY). */
export type OperationsStatsResponse = components["schemas"]["OperationStatsResponse"];

/**
 * One aggregated row of the per-domain extraction stats. Hand-written (the gateway route
 * is new and not yet in the regenerated OpenAPI types).
 */
export interface ExtractionDomainStat {
    domaine: string;
    total: number;
    ytg_profile: number;
    fallback_dom: number;
    fallback_http: number;
    blocked: number;
    taux_fallback_pct: number;
    taux_blocage_pct: number;
}

/** Response of the per-domain extraction stats endpoint. */
export interface ExtractionStatsResponse {
    items: ExtractionDomainStat[];
}

/** The website-extraction profiles the text extractor tool can run. */
export type ExtractionProfile =
    | "ytg_profile"
    | "ytg_profile_fast"
    | "trafilatura_rendered"
    | "trafilatura_classic";

/** Request body of the on-demand text extractor. */
export interface TextExtractionRequest {
    url: string;
    profile: ExtractionProfile;
}

/**
 * Result of a single-profile extraction. Hand-written (the gateway route is new and not
 * yet in the regenerated OpenAPI types).
 */
export interface TextExtractionResult {
    profile: string;
    url: string;
    text: string;
    word_count: number;
    http_status: number | null;
    content_type: string | null;
    outcome: string;
}

/** Filters accepted by /ytg-meta/operations/all. */
export interface OperationsListParams {
    skip?: number;
    limit?: number;
    client_id?: number;
    status?: OperationStatus;
    lang_id?: number;
    created_after?: string;
    created_before?: string;
    user_id?: number;
}

/** Body for creating a client (`POST /clients`). */
export type CreateClientPayload = components["schemas"]["ClientCreate"];

/** Response of `POST /ytg/operation/google-docs/access`. */
export type GoogleDocsAccessResponse = components["schemas"]["GoogleDocsAccessResponse"];

/** Response of `POST /ytg/operation/preview-sheet`. */
export type SheetPreviewStatsResponse = components["schemas"]["SheetPreviewStatsResponse"];

/** Body for updating a client (`PUT /clients/{id}`) — all fields optional. */
export type UpdateClientPayload = components["schemas"]["ClientUpdate"];

/** An active device session (own session list or admin view of a user's). */
export type SessionInfo = components["schemas"]["SessionInfo"];

/**
 * Outcome of revoking one Google grant.
 *
 * The two flags can disagree, and that is the point: `revoked` says the stored
 * tokens are gone, `revoked_at_google` says Google confirmed the grant itself is
 * gone. Deleted but unconfirmed means the grant may still be live and has to be
 * removed by hand from the Google account, so it must never be shown as a success.
 */
export interface GoogleRevocation {
    revoked: boolean;
    revoked_at_google: boolean;
}

/** Confirmation that an account was archived, and when. */
export type AccountArchived = components["schemas"]["AccountArchivedResponse"];

/** Outcome of the bulk revocation: how many were dropped, how many Google left unconfirmed. */
export interface BulkGoogleRevocation {
    revoked: number;
    unconfirmed: number;
}

/**
 * Typed facade over the Gateway REST API.
 *
 * Groups every gateway call (clients, YTG operation metadata, Drive/Sheets
 * exports, admin user management and device sessions) into a single object,
 * each method returning the unwrapped response payload.
 *
 * @example
 * ```ts
 * const clients = await GatewayService.getClients();
 * const created = await GatewayService.createClient({ name: "ACME", short_name: "ACME" });
 * const stats = await GatewayService.getYtgOperationsStats();
 * ```
 */
export const GatewayService = {
    // --- reads ---
    /** Fetch all clients. */
    getClients: (options: AxiosRequestConfig = {}): Promise<Client[]> =>
        gatewayApi.get<Client[]>(gatewayEndpoints.client(), options).then((r) => r.data),

    /** Fetch a single client by id. */
    getClientById: (id: number, options: AxiosRequestConfig = {}): Promise<Client> =>
        gatewayApi.get<Client>(gatewayEndpoints.clientById(id), options).then((r) => r.data),

    /** Fetch the operation-level metadata for a given YTG operation. */
    getYtgOperationMeta: (
        ytgOperationId: number | string,
        options: AxiosRequestConfig = {},
    ): Promise<OperationMetaItem> =>
        gatewayApi
            .get<OperationMetaItem>(gatewayEndpoints.ytgMetaByOperation(ytgOperationId), options)
            .then((r) => r.data),

    /** Fetch the per-row metadata list for a given YTG operation. */
    getYtgRowMetaByOperation: (ytgOperationId: number | string): Promise<unknown[]> =>
        gatewayApi
            .get<unknown[]>(gatewayEndpoints.ytgRowMetaByOperation(ytgOperationId))
            .then((r) => r.data),

    /** Get paginated operations with metadata (filtered server-side). */
    getYtgMetaOperationsAll: (
        params: OperationsListParams = {},
    ): Promise<PaginatedListResponse<OperationMetaItem>> => {
        const queryParams = new URLSearchParams();
        if (params.skip !== undefined) queryParams.append("skip", String(params.skip));
        if (params.limit !== undefined) queryParams.append("limit", String(params.limit));
        if (params.client_id !== undefined) queryParams.append("client_id", String(params.client_id));
        if (params.status !== undefined) queryParams.append("status", String(params.status));
        if (params.lang_id !== undefined) queryParams.append("lang_id", String(params.lang_id));
        if (params.created_after !== undefined) queryParams.append("created_after", params.created_after);
        if (params.created_before !== undefined) queryParams.append("created_before", params.created_before);
        if (params.user_id !== undefined) queryParams.append("user_id", String(params.user_id));

        const queryString = queryParams.toString();
        const url = queryString
            ? `${gatewayEndpoints.ytgMetaOperationsAll()}?${queryString}`
            : gatewayEndpoints.ytgMetaOperationsAll();

        return gatewayApi.get<PaginatedListResponse<OperationMetaItem>>(url).then((r) => r.data);
    },

    /** Get aggregated operation statistics computed server-side. */
    getYtgOperationsStats: (options: AxiosRequestConfig = {}): Promise<OperationsStatsResponse> =>
        gatewayApi
            .get<OperationsStatsResponse>(gatewayEndpoints.ytgMetaOperationsStats(), options)
            .then((r) => r.data),

    /** Fetch the per-domain website-extraction stats (admin only), optionally scoped to one operation. */
    getExtractionStats: (operationId?: number): Promise<ExtractionStatsResponse> =>
        gatewayApi
            .get<ExtractionStatsResponse>(gatewayEndpoints.extractionStats(), {
                params: operationId == null ? undefined : { operation_id: operationId },
            })
            .then((r) => r.data),

    /** Extract the text of a single URL with one profile (admin/tm). */
    postTextExtraction: (body: TextExtractionRequest): Promise<TextExtractionResult> =>
        gatewayApi
            .post<TextExtractionResult>(gatewayEndpoints.textExtraction(), body)
            .then((r) => r.data),

    // --- reads (sheets) ---
    /**
     * Fetch the column headers of a source spreadsheet.
     *
     * @param sheetName - Optional tab name; defaults to the first sheet.
     */
    getSheetHeaders: (spreadsheetId: string, sheetName: string | null = null): Promise<unknown> => {
        let url = gatewayEndpoints.ytgMetaSheetHeaders(spreadsheetId);
        if (sheetName) {
            url += `?sheet_name=${encodeURIComponent(sheetName)}`;
        }
        return gatewayApi.get<unknown>(url).then((r) => r.data);
    },

    // --- writes (operations from sheet / rows) ---
    /** Preview the rows that would be imported from a spreadsheet. */
    previewSheet: (body: Record<string, unknown>): Promise<SheetPreviewStatsResponse> =>
        gatewayApi
            .post<SheetPreviewStatsResponse>(gatewayEndpoints.ytgOperationPreviewSheet(), body)
            .then((r) => r.data),
    /** Sort Google Docs IDs into granted / not_granted / not_a_doc (1 to 500 IDs). */
    checkGoogleDocsAccess: (documentIds: string[]): Promise<GoogleDocsAccessResponse> =>
        gatewayApi
            .post<GoogleDocsAccessResponse>(gatewayEndpoints.ytgOperationGoogleDocsAccess(), {
                document_ids: documentIds,
            })
            .then((r) => r.data),
    /** Create a YTG operation from a Google Sheet source. */
    createOperationFromSheet: (body: Record<string, unknown>): Promise<unknown> =>
        gatewayApi.post<unknown>(gatewayEndpoints.ytgOperationFromSheet(), body).then((r) => r.data),
    /** Create a YTG operation from an explicit list of rows. */
    createOperationFromRows: (body: Record<string, unknown>): Promise<unknown> =>
        gatewayApi.post<unknown>(gatewayEndpoints.ytgOperationFromRows(), body).then((r) => r.data),

    // --- writes (clients) ---
    /** Create a new client. */
    createClient: (clientData: CreateClientPayload): Promise<Client> =>
        gatewayApi.post<Client>(gatewayEndpoints.client(), clientData).then((r) => r.data),
    /** Update an existing client by id. */
    updateClient: (id: number, clientData: UpdateClientPayload): Promise<Client> =>
        gatewayApi.put<Client>(gatewayEndpoints.clientById(id), clientData).then((r) => r.data),
    /** Delete a client by id. */
    deleteClient: (id: number): Promise<unknown> =>
        gatewayApi.delete<unknown>(gatewayEndpoints.clientById(id)).then((r) => r.data),

    // --- writes (other) ---
    /** Set the Google Drive target folder for an operation's uploads. */
    setYtgOperationFolder: (
        ytgOperationId: number | string,
        folderId: string,
        folderName: string,
    ): Promise<unknown> =>
        gatewayApi
            .post<unknown>(gatewayEndpoints.ytgMetaSetFolder(ytgOperationId), {
                folder_id: folderId,
                folder_name: folderName,
            })
            .then((r) => r.data),

    /**
     * Upload the guide PDFs of the given rows to Google Drive.
     *
     * @param folderId - Target Drive folder; defaults to the operation's folder.
     */
    uploadAllGuidesToDrive: (
        ytgOperationId: number | string,
        rowIds: number[],
        folderId: string | null = null,
    ): Promise<unknown> =>
        gatewayApi
            .post<unknown>(gatewayEndpoints.ytgUploadAllGuides(), {
                ytg_operation_id: ytgOperationId,
                row_ids: rowIds,
                folder_id: folderId,
            })
            .then((r) => r.data),

    /**
     * Upload the graph images of the given rows to Google Drive.
     *
     * @param folderId - Target Drive folder; defaults to the operation's folder.
     */
    uploadAllGraphsToDrive: (
        ytgOperationId: number | string,
        rowIds: number[],
        folderId: string | null = null,
    ): Promise<unknown> =>
        gatewayApi
            .post<unknown>(gatewayEndpoints.ytgUploadAllGraphs(), {
                ytg_operation_id: ytgOperationId,
                row_ids: rowIds,
                folder_id: folderId,
            })
            .then((r) => r.data),

    /**
     * Queue the asynchronous export of an operation to a Google Sheet.
     *
     * The export runs in a Celery worker; completion is delivered over WebSocket
     * (`sheet.export_completed`), not in this response.
     *
     * @returns The queued task descriptor (`task_id` + `status`).
     */
    exportYtgOperationToSheet: (
        ytgOperationId: number | string,
        options: CsvExportOptions = {},
    ): Promise<{ task_id: string; status: string }> =>
        gatewayApi
            .post<{ task_id: string; status: string }>(
                gatewayEndpoints.ytgMetaOperationSheets(ytgOperationId, options),
            )
            .then((r) => r.data),

    // --- downloads ---
    /** Build the browser-facing download URL for an operation's enriched CSV. */
    downloadYtgOperationEnrichedCsvUrl: (
        ytgOperationId: number | string,
        options: CsvExportOptions = {},
    ): string => `/api${gatewayEndpoints.ytgMetaOperationCsv(ytgOperationId, options)}`,

    // --- admin users ---
    /**
     * Admin: fetch all users.
     *
     * @param includeArchived - Include archived accounts, hidden by default.
     */
    getAdminUsers: (includeArchived = false): Promise<User[]> =>
        gatewayApi.get<User[]>(gatewayEndpoints.adminUsers(includeArchived)).then((r) => r.data),
    /** Admin: change a user's role. */
    updateUserRole: (userId: number, role: UserRole): Promise<unknown> =>
        gatewayApi
            .put<unknown>(gatewayEndpoints.adminUserRole(userId), { role })
            .then((r) => r.data),
    /** Admin: activate or deactivate a user. */
    updateUserActive: (userId: number, isActive: boolean): Promise<unknown> =>
        gatewayApi
            .put<unknown>(gatewayEndpoints.adminUserActive(userId), { is_active: isActive })
            .then((r) => r.data),
    /** Admin: revoke all sessions of a given user. */
    revokeUserSessions: (userId: number): Promise<unknown> =>
        gatewayApi.post<unknown>(gatewayEndpoints.adminUserRevokeSessions(userId)).then((r) => r.data),
    /** Admin: revoke every session of every user (global logout). */
    revokeAllSessions: (): Promise<unknown> =>
        gatewayApi.post<unknown>(gatewayEndpoints.adminRevokeAllSessions()).then((r) => r.data),

    // --- admin Google grants ---
    /**
     * Admin: revoke a user's Google grant and delete their stored tokens.
     *
     * Leaves their toolbox sessions untouched: this disconnects Google, it does not
     * log the user out. Resolves even when the user had no tokens stored.
     */
    revokeUserGoogleToken: (userId: number): Promise<GoogleRevocation> =>
        gatewayApi
            .post<GoogleRevocation>(gatewayEndpoints.adminUserRevokeGoogleToken(userId))
            .then((r) => r.data),
    /**
     * Admin: revoke every user's Google grant and delete the stored tokens.
     *
     * @returns How many users were revoked, and how many of those Google did not
     *   confirm — those grants may still be live and need removing by hand.
     */
    revokeAllGoogleTokens: (): Promise<BulkGoogleRevocation> =>
        gatewayApi
            .post<BulkGoogleRevocation>(gatewayEndpoints.adminRevokeAllGoogleTokens())
            .then((r) => r.data),

    /**
     * Admin: archive an account.
     *
     * Offboarding: deactivates it, closes every session, revokes the Google grant and
     * strips the personal data from the row. Replaces account deletion, which the
     * database refuses since operations reference their owner. Irreversible.
     */
    archiveUser: (userId: number): Promise<AccountArchived> =>
        gatewayApi
            .post<AccountArchived>(gatewayEndpoints.adminUserArchive(userId))
            .then((r) => r.data),

    // --- device sessions (self-service) ---
    /** Fetch the current user's own active device sessions. */
    getSessions: (options: AxiosRequestConfig = {}): Promise<SessionInfo[]> =>
        gatewayApi.get<SessionInfo[]>(gatewayEndpoints.authSessions(), options).then((r) => r.data),
    /** Revoke one of the current user's own device sessions. */
    revokeSession: (sessionId: string): Promise<unknown> =>
        gatewayApi.post<unknown>(gatewayEndpoints.authRevokeSession(sessionId)).then((r) => r.data),

    // --- Google grant (self-service) ---
    /**
     * Disconnect the current user's Google account.
     *
     * Revokes the grant at Google and deletes the tokens stored server-side, so
     * nothing can reach the user's Drive until they authorize the application again
     * at their next login. The toolbox session stays open.
     */
    disconnectGoogleAccount: (): Promise<GoogleRevocation> =>
        gatewayApi
            .delete<GoogleRevocation>(gatewayEndpoints.authGoogleToken())
            .then((r) => r.data),

    // --- device sessions (admin, for a given user) ---
    /** Admin: fetch a given user's active device sessions. */
    getUserSessions: (userId: number, options: AxiosRequestConfig = {}): Promise<SessionInfo[]> =>
        gatewayApi.get<SessionInfo[]>(gatewayEndpoints.adminUserSessions(userId), options).then((r) => r.data),
    /** Admin: revoke one specific device session of a given user. */
    adminRevokeUserSession: (userId: number, sessionId: string): Promise<unknown> =>
        gatewayApi.post<unknown>(gatewayEndpoints.adminRevokeUserSession(userId, sessionId)).then((r) => r.data),

    // --- utilities ---
    /**
     * Build a gateway-proxied URL for a remote image.
     *
     * @returns The proxied URL, or `undefined` when no image URL is given.
     */
    getProxiedImageUrl: (imageUrl: string | null | undefined): string | undefined => {
        if (!imageUrl) return undefined;
        return `/api${gatewayEndpoints.proxyImage(imageUrl)}`;
    },
};
